/**
 * =============================================================================
 * Hook: useAffiliateRequestPayout
 * Versão: 2.0.0 (Server-side hardening — RPC transacional)
 *
 * Mudanças v2.0.0:
 *   - Cliente NÃO escolhe mais comissões. Passa apenas `programId` +
 *     método/detalhes de pagamento. O servidor agrupa todas as comissões
 *     elegíveis daquele programa, com lock pessimista contra saques duplos
 *     (ver RPC `request_affiliate_payout_v2`).
 *   - Saldo é recalculado server-side a partir do ledger — colunas
 *     agregadas (available_balance) são apenas cache informativo.
 *   - Idempotência: existe índice único parcial impedindo dois payouts
 *     pending/processing por (affiliate_id, membership_id).
 * =============================================================================
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

const log = createLogger('useAffiliateRequestPayout');

export type PayoutMethod =
  Database['public']['Enums']['affiliate_payout_method'];

export interface RequestPayoutParams {
  /** Programa do qual sacar todo o saldo aprovado disponível. */
  programId: string;
  /** Método de pagamento (default: pix). */
  method?: PayoutMethod;
  /** Detalhes do método (chave PIX, dados bancários, etc). */
  paymentDetails?: Record<string, unknown>;
  /** Notas opcionais. */
  notes?: string | null;
}

export interface RequestPayoutResult {
  payout_id: string;
  payout_id_short: string;
  amount: number;
  commissions_count: number;
  currency: string;
}

export function useAffiliateRequestPayout() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (params: RequestPayoutParams): Promise<RequestPayoutResult> => {
      if (!params.programId) {
        throw new Error('programId é obrigatório');
      }

      const { data, error } = await supabase.rpc('request_affiliate_payout_v2', {
        p_program_id: params.programId,
        p_payout_method: params.method ?? 'pix',
        p_payment_details: (params.paymentDetails ?? {}) as never,
        p_notes: params.notes ?? null,
      });

      if (error) {
        log.error('Falha ao solicitar payout (v2)', error);
        throw error;
      }
      return data as unknown as RequestPayoutResult;
    },
    onSuccess: () => {
      // Invalida tudo que pode mostrar saldo / payouts.
      qc.invalidateQueries({ queryKey: ['affiliate-home-overview'] });
      qc.invalidateQueries({ queryKey: ['affiliate-payouts'] });
      qc.invalidateQueries({ queryKey: ['affiliate-commissions'] });
      qc.invalidateQueries({ queryKey: ['affiliate-available-commissions'] });
    },
  });
}
